import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { CardGiftcard, Send, AccessTime, People, Telegram, Payments } from '@mui/icons-material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { GiftBonusPreview } from '../../types';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-ET', { style: 'currency', currency: 'ETB' }).format(n);

const PRESET_AMOUNTS = [10, 25, 50, 100, 200];

const GiftBonusPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [preview, setPreview] = useState<GiftBonusPreview | null>(null);
  const [amount, setAmount] = useState(10);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notifyTelegram, setNotifyTelegram] = useState(true);
  const [notifyPush, setNotifyPush] = useState(true);

  const [cooldownLeftMs, setCooldownLeftMs] = useState(0);

  const loadPreview = useCallback(async (value = amount) => {
    setLoadingPreview(true);
    try {
      const res = await apiService.previewGiftBonus(value);
      if (res.success && res.data) {
        setPreview(res.data);
        setCooldownLeftMs(res.data.cooldownRemainingMs || 0);
      }
    } catch {
      toast.error('Could not load gift bonus preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [amount]);

  useEffect(() => {
    loadPreview(amount);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cooldownLeftMs <= 0) return undefined;
    const timer = setInterval(() => {
      setCooldownLeftMs((ms) => Math.max(0, ms - 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownLeftMs > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePresetSelect = (val: number) => {
    setAmount(val);
    loadPreview(val);
  };

  const openConfirm = async () => {
    await loadPreview(amount);
    setDialogOpen(true);
  };

  const handleSend = async () => {
    setSubmitting(true);
    try {
      const res = await apiService.giftBonusToAllPlayers({
        amount,
        confirm: true,
        notifyTelegram,
        notifyPush,
      });
      if (res.success) {
        toast.success(res.message || 'Bonus sent to all players');
        setDialogOpen(false);
        setCooldownLeftMs(5 * 60 * 1000);
        await loadPreview(amount);
      } else {
        toast.error(res.error || 'Failed to send bonus');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to send bonus');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendTelegram = async () => {
    if (!preview?.lastCampaign?.id) return;
    setResending(true);
    try {
      const res = await apiService.resendGiftTelegram(preview.lastCampaign.id);
      if (res.success) {
        toast.success(res.message || 'Telegram is sending. Bonus will not be credited again.');
        await loadPreview(amount);
      } else {
        toast.error(res.error || 'Failed to resend Telegram');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to resend Telegram');
    } finally {
      setResending(false);
    }
  };

  const cooldownSec = Math.ceil(cooldownLeftMs / 1000);
  const onCooldown = cooldownLeftMs > 0;
  const last = preview?.lastCampaign;
  const telegramPending = Boolean(last && (last.telegramPending ?? !last.telegramStats));

  return (
    <>
      <Card
        sx={{
          mb: 3,
          borderRadius: { xs: 2.5, sm: 3 },
          border: '1px solid rgba(139, 92, 246, 0.35)',
          background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.45) 0%, rgba(20, 20, 35, 0.85) 100%)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          {/* Header row */}
          <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
            <Avatar
              sx={{
                background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                boxShadow: '0 4px 12px rgba(236, 72, 153, 0.35)',
              }}
            >
              <CardGiftcard sx={{ fontSize: { xs: 22, sm: 26 } }} />
            </Avatar>
            <Box flex={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                  Platform Bonus Gift
                </Typography>
                {onCooldown && (
                  <Chip
                    icon={<AccessTime fontSize="small" />}
                    label={`Wait ${cooldownSec}s`}
                    size="small"
                    color="warning"
                    sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
                Credit instant bonus balance to active players & notify via Telegram.
              </Typography>
            </Box>
          </Box>

          {/* Preset amount selector chips */}
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Select Amount (ETB)
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {PRESET_AMOUNTS.map((val) => (
                <Chip
                  key={val}
                  label={`${val} ETB`}
                  clickable
                  onClick={() => handlePresetSelect(val)}
                  color={amount === val ? 'primary' : 'default'}
                  variant={amount === val ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: amount === val ? 700 : 500,
                    fontSize: { xs: '0.75rem', sm: '0.82rem' },
                    height: { xs: 32, sm: 36 },
                    px: 0.5,
                    transition: 'all 0.15s ease',
                  }}
                />
              ))}

              <TextField
                type="number"
                size="small"
                value={amount}
                onChange={(e) => setAmount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                onBlur={() => loadPreview(amount)}
                inputProps={{ min: 1, max: 1000 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.secondary">ETB</Typography></InputAdornment>,
                }}
                sx={{
                  width: { xs: 110, sm: 120 },
                  '& .MuiInputBase-root': { height: { xs: 32, sm: 36 }, fontSize: '0.82rem' },
                }}
              />
            </Box>
          </Box>

          {/* Quick stats badges */}
          {preview ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, auto)' },
                gap: { xs: 1, sm: 1.5 },
                mb: 2,
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
              }}
            >
              <Box display="flex" alignItems="center" gap={0.75}>
                <People sx={{ fontSize: { xs: 16, sm: 18 }, color: 'primary.light' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                    Eligible
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
                    {preview.eligibleUsers.toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={0.75}>
                <Telegram sx={{ fontSize: { xs: 16, sm: 18 }, color: '#38bdf8' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                    Telegram
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.78rem', sm: '0.85rem' } }}>
                    ~{preview.withTelegram.toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={0.75}>
                <Payments sx={{ fontSize: { xs: 16, sm: 18 }, color: '#34d399' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                    Est. Cost
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.78rem', sm: '0.85rem' }, color: '#34d399' }}>
                    {fmt(preview.eligibleUsers * amount)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : loadingPreview ? (
            <Box display="flex" alignItems="center" gap={1} mb={2} py={1}>
              <CircularProgress size={18} />
              <Typography variant="caption" color="text.secondary">Calculating preview...</Typography>
            </Box>
          ) : null}

          {/* Last Campaign Alert */}
          {last && (
            <Alert
              severity={telegramPending ? 'warning' : 'info'}
              sx={{
                mb: 2,
                py: 0.5,
                px: 1.5,
                fontSize: { xs: '0.75rem', sm: '0.82rem' },
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
                <Typography variant="caption" fontWeight={700}>
                  Last gift: {last.amount} ETB to {last.usersCredited.toLocaleString()} players
                </Typography>
                {last.at && (
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(last.at).format('MMM D, h:mm A')}
                  </Typography>
                )}
              </Box>
              {telegramPending && (
                <Typography variant="caption" display="block" color="warning.main" sx={{ mt: 0.25, fontWeight: 600 }}>
                  Telegram delivery is still pending. Use "Resend Telegram" without double-crediting.
                </Typography>
              )}
            </Alert>
          )}

          {/* Action buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems="stretch"
            sx={{ width: '100%' }}
          >
            {telegramPending && last && (
              <Button
                variant="outlined"
                color="warning"
                size={isMobile ? 'medium' : 'small'}
                startIcon={resending ? <CircularProgress size={16} /> : <Send />}
                onClick={handleResendTelegram}
                disabled={resending || submitting}
                sx={{ flex: { xs: 1, sm: 'none' }, py: { xs: 1, sm: 0.75 } }}
              >
                {resending ? 'Resending…' : 'Resend Telegram Only'}
              </Button>
            )}

            <Button
              variant="contained"
              color="secondary"
              size={isMobile ? 'medium' : 'small'}
              startIcon={<CardGiftcard />}
              onClick={openConfirm}
              disabled={onCooldown || submitting || loadingPreview}
              sx={{
                flex: 1,
                py: { xs: 1.1, sm: 0.75 },
                fontWeight: 700,
                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #db2777, #7c3aed)',
                },
              }}
            >
              Gift {amount} ETB to All Players
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.2rem' } }}>
          Confirm Platform Bonus
        </DialogTitle>
        <DialogContent dividers>
          {preview && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Credit <strong>{amount} ETB</strong> to{' '}
                <strong>{preview.eligibleUsers.toLocaleString()}</strong> active players.
                <br />
                Total Platform Cost: <strong>{fmt(preview.eligibleUsers * amount)}</strong>
              </Alert>

              <Stack spacing={1} sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifyTelegram}
                      onChange={(e) => setNotifyTelegram(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      Send Telegram Alert (~{preview.withTelegram.toLocaleString()} players)
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifyPush}
                      onChange={(e) => setNotifyPush(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      Send Web Push Alert (subscribed devices)
                    </Typography>
                  }
                />
              </Stack>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                ⚠️ Bots are automatically excluded. This action creates balance transactions and cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} sx={{ flex: { xs: 1, sm: 'none' } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSend}
            disabled={submitting || !preview}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{
              flex: { xs: 1, sm: 'none' },
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
            }}
          >
            {submitting ? 'Sending…' : `Send ${amount} ETB`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GiftBonusPanel;
